import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import io from "socket.io-client";
import { FaPaperPlane, FaExclamationTriangle, FaComments, FaHandPaper } from "react-icons/fa";
import { SkeletonSearching } from "../../Skeleton";
import API_URL from '../../../utils/api';
import "./TextChat.css";

export default function TextChat() {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatId } = useParams();

  const [socket, setSocket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [eqWarning, setEqWarning] = useState(null);
  
  // Ready state
  const [partnerReady, setPartnerReady] = useState(false);
  const [bothReady, setBothReady] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(120); // 2 minutes

  const [sessionComplete, setSessionComplete] = useState(false);

  const messagesEndRef = useRef(null);
  const userId = sessionStorage.getItem('userId') || localStorage.getItem('userId');

  const {
    partner,
    yourProfile,
    conversationStarter
  } = location.state || {};

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Timer countdown when waiting for partner
  useEffect(() => {
    if (!bothReady && !partnerReady && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            // Navigate back to lobby when timer expires
            navigate('/funtellime/text-lobby');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [bothReady, partnerReady, timeRemaining, navigate]);

  // Initialize Socket.io
  useEffect(() => {
    if (!chatId || !userId) return;

    const newSocket = io(API_URL);


    newSocket.on("connect", () => {
      newSocket.emit("join-funtellime-chat", { chatId, userId });
      
      // Mark this user as ready
      fetch(`${API_URL}/api/funtellime/session/${chatId}/ready`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: parseInt(userId) })
      })
      .then(res => {
        return res.json();
      })
      .then(data => {
        
        if (data.bothReady) {
          setBothReady(true);
          setPartnerReady(true);
        } else {
          // Check partner's ready state from response
          const currentUserId = parseInt(userId);
          const isUser1 = currentUserId === parseInt(data.user1);
          const partnerIsReady = isUser1 ? data.user2Ready : data.user1Ready;
          setPartnerReady(partnerIsReady || false);
        }
      })
      .catch(err => {
      });
    });

    // Receive messages
    newSocket.on("funtellime-message", (data) => {
      setMessages(prev => [...prev, data]);
    });

    // EQ mistake warning
    newSocket.on("funtellime-eq-warning", (data) => {
      if (data.userId === parseInt(userId)) {
        setEqWarning(data.mistake);
        setTimeout(() => setEqWarning(null), 15000);
      }
    });
    
    // Ready state updates
    newSocket.on("funtellime-ready-update", (data) => {
      const currentUserId = parseInt(userId);
      const isUser1 = currentUserId === parseInt(data.user1);
      const partnerIsReady = isUser1 ? data.user2Ready : data.user1Ready;
      setPartnerReady(partnerIsReady);
    });
    
    // Both users ready
    newSocket.on("funtellime-both-ready", (data) => {
      setBothReady(true);
    });

    // Session complete
    newSocket.on("funtellime-session-complete", async () => {
      setSessionComplete(true);
      
      // End session and save to DB
      try {
        await fetch(`${API_URL}/api/funtellime/session/${chatId}/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: parseInt(userId) })
        });
      } catch (error) {
      }
      
      setTimeout(() => {
        navigate(`/funtellime/end/${chatId}`, {
          state: { reason: 'completed', chatId, partner }
        });
      }, 1000);
    });
    
    // Partner ended chat
    newSocket.on("funtellime-partner-ended", async () => {
      setSessionComplete(true);
      
      // End session and save to DB
      try {
        await fetch(`${API_URL}/api/funtellime/session/${chatId}/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: parseInt(userId) })
        });
      } catch (error) {
      }
      
      setTimeout(() => {
        navigate(`/funtellime/end/${chatId}`, {
          state: { reason: 'partner-ended', chatId, partner }
        });
      }, 500);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [chatId, userId, navigate, partner]);

  // Send message
  const handleSendMessage = () => {
    if (!inputMessage.trim() || !socket || sessionComplete) return;

    const messageData = {
      chatId,
      userId: parseInt(userId),
      userName: yourProfile?.name || "You",
      message: inputMessage.trim(),
      timestamp: Date.now()
    };

    socket.emit("funtellime-send-message", messageData);
    setMessages(prev => [...prev, messageData]);
    setInputMessage("");
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // End chat manually
  const handleEndChat = async () => {
    if (socket && !sessionComplete) {
      setSessionComplete(true);
      // Notify partner that this user ended the chat
      socket.emit('funtellime-end-chat', { chatId, userId: parseInt(userId) });
      
      // End the session and save data to database
      try {
        await fetch(`${API_URL}/api/funtellime/session/${chatId}/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: parseInt(userId) })
        });
      } catch (error) {
      }
      
      // Navigate to end screen after saving
      setTimeout(() => {
        navigate(`/funtellime/end/${chatId}`, {
          state: { reason: 'manual', chatId, partner }
        });
      }, 500);
    }
  };

  // Removed Done Sharing functionality - users can chat freely

  if (!partner || !yourProfile) {
    navigate("/funtellime/text-lobby");
    return null;
  }

  // Wait for conversation starter to be initialized
  if (!conversationStarter) {
    return (
      <div className="text-chat-container">
        <SkeletonSearching />
      </div>
    );
  }

  const currentPrompt = conversationStarter || "Loading...";


  return (
    <div className="text-chat-container">
      {/* Header */}
      <div className="chat-header">
        <div className="chat-header-left">
          <div className="partner-avatar">
            <img src={partner.profilePic || 'https://via.placeholder.com/50'} alt={partner.name} />
            {!sessionComplete && bothReady && <div className="online-indicator"></div>}
          </div>
          <div className="partner-info">
            <h3>{partner.name}</h3>
            {!bothReady ? (
              <p className="waiting-status">
                Waiting for {partner.name}... 
                <span className="waiting-timer">
                  {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
                </span>
              </p>
            ) : (
              <p>{sessionComplete ? "Session complete" : "Online"}</p>
            )}
          </div>
        </div>
        <div className="chat-header-right">
          <button className="end-chat-btn" onClick={handleEndChat} disabled={sessionComplete}>
            End Chat
          </button>
        </div>
      </div>

      {/* Current Conversation Starter */}
      <div className="i-share-prompt-card">
        <div className="prompt-header">
          <FaComments className="prompt-icon" />
          <span>Conversation Starter</span>
        </div>
        <p className="prompt-text">{currentPrompt}</p>
        <div className="turn-indicator">
          <span className="prompt-info"><FaComments /> Chat freely and get to know each other!</span>
        </div>
      </div>

      {/* EQ Warning */}
      {eqWarning && (
        <div className="eq-warning-toast">
          <span className="warning-icon"><FaExclamationTriangle /></span>
          <div>
            <strong>{eqWarning.title}</strong>
            <p>{eqWarning.description}</p>
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div className="messages-container">
        {messages.length === 0 && (
          <div className="welcome-message">
            <p><FaHandPaper /> Start chatting and get to know each other!</p>
          </div>
        )}
        {messages.map((msg, index) => {
          const isOwnMessage = msg.userId === parseInt(userId);
          return (
            <div 
              key={index} 
              className={`message ${isOwnMessage ? 'own-message' : 'partner-message'}`}
            >
              <div className="message-bubble">
                <p>{msg.message}</p>
                <span className="message-time">
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="chat-input-container">
        <textarea
          className="chat-input"
          placeholder={!bothReady ? "Waiting for partner to join..." : sessionComplete ? "Session complete" : "Type your message..."}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={sessionComplete || !bothReady}
          rows={2}
        />
        <button 
          className="send-btn" 
          onClick={handleSendMessage}
          disabled={!inputMessage.trim() || sessionComplete || !bothReady}
        >
          Send <FaPaperPlane style={{ marginLeft: '4px' }} />
        </button>
      </div>

      {/* Session Complete Overlay */}
      {sessionComplete && (
        <div className="chat-ended-overlay">
          <div className="ended-message">
            <h2>Great Job</h2>
            <p>Great conversation! Redirecting to summary...</p>
          </div>
        </div>
      )}
    </div>
  );
}
